import {HttpClient, HttpHeaders} from '@angular/common/http';
import {inject} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {CanActivateFn, Router} from '@angular/router';
import {
  ConfigService,
  DialogWidth,
  handleError,
  SessionService,
  ThemePalette
} from '@q9elements/ui-kit/common';
import {addHours, getHours, setHours, setMinutes, setSeconds} from 'date-fns';
import {get} from 'lodash';
import {catchError, finalize, map, mergeMap, Observable, of, switchMap, tap} from 'rxjs';

import {SfConnectionErrorComponent} from '../dialogs/sf-connection-error/sf-connection-error.component';
import {SfConnectionInfoDialogComponent} from '../dialogs/sf-connection-info/sf-connection-info-dialog.component';
import {
  ALREADY_CONNECTED_ERR,
  ConnectOrgResponse,
  GeneralOrgInfoResponse,
  OrgGeneralInfoPayload,
  SfCallbackParams
} from '../models/salesforce-orgs.interface';
import {sfCallbackParser} from '../utils/sf-callback-parser';
import {getAndRemovePkceVerifierByState} from '../utils/sf-pkce-storage';

export const SalesforceConnectCallbackGuard: CanActivateFn = route => {
  const router = inject(Router);
  const http = inject(HttpClient);
  const dialog = inject(MatDialog);
  const sessionService = inject(SessionService);
  const parsedSfQueryParams: SfCallbackParams = sfCallbackParser(
    route.queryParams as SfCallbackParams
  );
  const {
    teamId,
    implementationId,
    isSandbox,
    code,
    reconnectOrg,
    teamIndex,
    type = 'external',
    manualSyncOnlyEnabled,
    isNewImplementation
  } = parsedSfQueryParams;
  const state = String(route.queryParams['state'] ?? '');

  parsedSfQueryParams['oldOrgId'] = parsedSfQueryParams.orgId;
  const isReconnect = reconnectOrg && reconnectOrg === 'true';
  const headers = new HttpHeaders({'el-team-id': String(teamId), 'el-team': String(teamIndex)});
  let orgGeneralInfo: any = {};
  let orgDeleted = false;

  const deleteOrgImplementation = (): Observable<any> => {
    if (isNewImplementation !== 'true' || orgDeleted) {
      return of(null);
    }

    orgDeleted = true;
    const url = `${ConfigService.environment.API_ENDPOINT}/organizations/${teamId}/implementations/${implementationId}`;

    return http.delete(url, {headers}).pipe(catchError(() => of(null)));
  };

  const returnBackToReferenceModels = (
    queryParams:
      | {
          orgId: string;
          message: string;
        }
      | object = {}
  ) => router.navigate([`/t/${+teamIndex}/referencemodels-external`], {queryParams});

  const openSfConnectionIfoDialog = (payload: OrgGeneralInfoPayload) => {
    const successData = {
      orgId: payload.orgId || 'N/A',
      username: payload.username || 'N/A',
      syncTime: payload.uiTime,
      orgDomain: payload.instance_url,
      isSandbox: isSandbox === 'true',
      spaceId: teamId,
      manualSyncOnlyEnabled: manualSyncOnlyEnabled === 'true'
    };

    return dialog
      .open(SfConnectionInfoDialogComponent, {
        width: DialogWidth.MD,
        disableClose: !isReconnect,
        data: {
          ...payload,
          color: ThemePalette.PRIMARY,
          ...successData
        }
      })
      .afterClosed();
  };

  const isSFError = (error: any) => {
    if (typeof error.error === 'string') {
      return false;
    }

    // for eslint: no-prototype-builtins
    return (
      Object.prototype.hasOwnProperty.call(error.error, 'error') &&
      Object.prototype.hasOwnProperty.call(error.error, 'error_description')
    );
  };

  const isOverriddenErrorFromSF = (error: any) => {
    const overriddenErrors = ['invalid_username_or_pass'];

    return overriddenErrors.includes(error?.status);
  };

  const getErrorPayload = (error: any, from: string) => {
    if (isSFError(error)) {
      return {errorData: error?.error, errorSource: 'SF_AUTH'};
    } else if (isOverriddenErrorFromSF(error)) {
      return {
        errorData: {error: error.error, error_description: error?.status},
        errorSource: 'SF_AUTH'
      };
    } else {
      return {errorData: error, errorSource: from};
    }
  };

  const catchErrorHandler = (from: 'OAUTH' | 'Elements') => {
    return catchError(error => {
      const errorPayload = getErrorPayload(error, from);

      return dialog
        .open(SfConnectionErrorComponent, {
          width: DialogWidth.MD,
          data: {
            color: ThemePalette.PRIMARY,
            username: orgGeneralInfo.username || 'Could not resolve',
            connectedUsername: orgGeneralInfo.connectedUsername,
            connectedSpaceName: orgGeneralInfo.connectedSpaceName,
            orgId: orgGeneralInfo.orgId || 'Could not resolve',
            orgDomain: orgGeneralInfo.instance_url || 'Could not resolve',
            isSandbox: isSandbox === 'true',
            error: errorPayload
          }
        })
        .afterClosed()
        .pipe(
          switchMap(() => deleteOrgImplementation()),
          tap(() => returnBackToReferenceModels()),
          finalize(() => sessionService.setLoading(false))
        );
    });
  };

  const {uiTime, backEndTime} = getRandomScheduleTime();
  const generalOrgInfoUrl = getGeneralOrgInfoUrl(teamId, implementationId);
  const codeVerifier = getAndRemovePkceVerifierByState(state);

  sessionService.setLoading();

  return http
    .post<GeneralOrgInfoResponse>(generalOrgInfoUrl, {code, isSandbox, codeVerifier}, {headers})
    .pipe(
      mergeMap(({result, isSuccess}) => {
        orgGeneralInfo = {uiTime, ...parsedSfQueryParams, ...result, isSuccess};
        const isConnectOrg = isSuccess && !result.alreadyConnected && !isReconnect;

        const connectOrg = () => {
          const connectOrgPayload = {
            hour: backEndTime,
            implementationId,
            username: result.username,
            orgId: result.orgId,
            teamId
          };
          const orgType = isSandbox === 'false' ? 'connectProduction' : 'connectSandbox';
          const connectOrgUrl = getConnectOrgUrl(teamId, implementationId, orgType);

          return http.put<ConnectOrgResponse>(connectOrgUrl, connectOrgPayload, {headers}).pipe(
            tap(connectOrgResponse => {
              const refModelId = get(connectOrgResponse, 'refModelId');

              if (!refModelId) {
                return;
              }

              const syncUrl = `${ConfigService.environment.API_ENDPOINT}/refmodel/${type}/structure/sync/${refModelId}`;

              const startSync = () => {
                http.post(syncUrl, null, {headers}).pipe(handleError()).subscribe();
              };

              const manualSyncOnly = manualSyncOnlyEnabled === 'true';

              if (manualSyncOnly) {
                http
                  .put(
                    `${ConfigService.environment.API_ENDPOINT}/refmodel/${refModelId}/toggle-update/manualSyncOnlyEnabled`,
                    {
                      id: refModelId,
                      manualSyncOnlyEnabled: true
                    },
                    {headers}
                  )
                  .pipe(catchError(() => of(null)))
                  .subscribe({
                    next: startSync,
                    error: startSync
                  });
              } else {
                startSync();
              }
            }),
            map(connectOrgResponse => ({...orgGeneralInfo, ...connectOrgResponse})),
            catchErrorHandler('OAUTH')
          );
        };

        return isConnectOrg ? connectOrg() : of(orgGeneralInfo);
      }),
      switchMap((orgGeneralInfo: OrgGeneralInfoPayload) => {
        if (orgGeneralInfo.alreadyConnected) {
          throw {
            error: ALREADY_CONNECTED_ERR
          };
        }

        if (!orgGeneralInfo.isSuccess) {
          return deleteOrgImplementation().pipe(switchMap(() => returnBackToReferenceModels()));
        }

        return openSfConnectionIfoDialog(orgGeneralInfo).pipe(
          switchMap(() => {
            return returnBackToReferenceModels(
              orgGeneralInfo?.alreadyConnected ? {} : {orgId: orgGeneralInfo.orgId}
            );
          })
        );
      }),
      finalize(() => sessionService.setLoading(false)),
      catchErrorHandler('Elements')
    );
};

const getGeneralOrgInfoUrl = (teamId: string, implementationId: string) => {
  const domain = `${ConfigService.environment.API_ENDPOINT}/organizations/${teamId}`;
  const endPoint = `implementations/${implementationId}/generalSfOrgInfo`;

  return `${domain}/${endPoint}`;
};

const getConnectOrgUrl = (teamId: string, implementationId: string, orgType: string) => {
  const domain = `${ConfigService.environment.API_ENDPOINT}/organizations/${teamId}`;
  const endPoint = `implementations/${implementationId}/${orgType}`;

  return `${domain}/${endPoint}`;
};

const getRandomNumber = () => {
  const startDate = setHours(new Date(), 21);
  const endDate = setHours(new Date(), 7);
  const randomHoursForScheduling = [];

  let currentHour = startDate;

  while (getHours(currentHour) !== getHours(endDate)) {
    randomHoursForScheduling.push(getHours(currentHour));
    currentHour = addHours(currentHour, 1);
  }

  // Handle the case where startDate is after endDate (crosses midnight)
  if (getHours(currentHour) === getHours(endDate)) {
    randomHoursForScheduling.push(getHours(currentHour));
  }

  const num = Math.floor(Math.random() * Math.floor(randomHoursForScheduling.length - 1));

  return randomHoursForScheduling[num];
};

/** This method will get random time between 9pm(21:00) and 7am(7:00) hour and return object
 * {
 *   uiTime: time in users time zone (simple string: '1:00') - need to show it in dialog
 *   backEndTime: moment time in utc - will send on backend for save in DB
 * } */
const getRandomScheduleTime = () => {
  const randomHour = getRandomNumber();
  const uiTime = `${randomHour}:00`;
  const value = uiTime.split(':')[0];
  const backEndTime = setSeconds(setMinutes(setHours(new Date(), +value), 0), 0);

  return {backEndTime, uiTime};
};
